import { useEffect, useState } from 'react';
import { Search, Download, FileSpreadsheet, FileText, Image as ImageIcon, Film, ExternalLink, X, Play } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { api, asset } from '../../lib/project';
import { ReferenceGallery } from './ReferenceGallery';

// Media preview overlay rendered on top of the Materials body. Closing the
// preview only clears `preview` — it does not close the Materials dialog.
const MediaPreview = ({ preview, onClose }) => {
  if (!preview) return null;
  return (
    <div className="materials-preview" role="dialog" aria-modal="true" data-testid="materials-preview">
      <button type="button" className="materials-preview-close" onClick={onClose} aria-label="Fechar pré-visualização" data-testid="materials-preview-close">
        <X size={20} />
      </button>
      <div className="materials-preview-head">
        <strong>{preview.title}</strong>
        <a href={asset(preview.id, true)} target="_blank" rel="noopener noreferrer" data-testid="materials-preview-download">
          <Download size={15} /> Descarregar
        </a>
      </div>
      <div className="materials-preview-body">
        {preview.type === 'video' ? (
          <video src={asset(preview.id)} controls autoPlay playsInline data-testid="materials-preview-video">
            O seu navegador não suporta reprodução em linha.
          </video>
        ) : (
          <img src={asset(preview.id)} alt={preview.title} data-testid="materials-preview-image" />
        )}
      </div>
    </div>
  );
};

export const Materials = ({ open, onOpenChange }) => {
  const [tab, setTab] = useState('materials');
  const [filter, setFilter] = useState('Todos');
  const [search, setSearch] = useState('');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.get('/assets');
      setFiles(data.sort((a, b) => (a.id === 'projection-xlsx' ? -1 : b.id === 'projection-xlsx' ? 1 : 0)));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open]);
  // Reset the preview whenever the modal is closed, but never automatically
  // close the parent when the preview is dismissed.
  useEffect(() => { if (!open) setPreview(null); }, [open]);

  const openMedia = (f) => {
    if (f.category === 'Vídeos') setPreview({ id: f.id, title: f.title, type: 'video' });
    else if (f.category === 'Fotografias') setPreview({ id: f.id, title: f.title, type: 'image' });
  };

  const filtered = files.filter((f) =>
    (filter === 'Todos' || f.category === filter) &&
    (f.title + ' ' + f.original_filename).toLocaleLowerCase('pt').includes(search.toLocaleLowerCase('pt'))
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="custom-dialog materials-dialog" data-testid="materials-dialog">
        <div className="modal-eyebrow" data-testid="materials-eyebrow">LIOFILÂNDIA / SALA DE MATERIAIS</div>
        <DialogTitle data-testid="materials-title">Uma visão. Fontes à vista.</DialogTitle>
        <DialogDescription data-testid="materials-description">Consulte os documentos fornecidos e as referências ao plano.</DialogDescription>
        <div className="modal-main-tabs">
          {[['materials', 'Materiais'], ['gallery', 'Referências']].map(([v, label]) => (
            <button data-testid={`materials-tab-${v}`} className={tab === v ? 'selected' : ''} key={v} onClick={() => setTab(v)}>{label}</button>
          ))}
        </div>
        <div className="materials-body" data-lenis-prevent>
          {tab === 'materials' && (
            <>
              <div className="file-search">
                <Search size={17} />
                <input data-testid="materials-search-input" aria-label="Pesquisar materiais" placeholder="Pesquisar materiais…" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <div className="filter-tabs file-filters">
                {['Todos', 'Financeiros', 'Documentos', 'Fotografias', 'Vídeos'].map((f, i) => (
                  <button key={f} data-testid={`materials-filter-${i}`} className={filter === f ? 'selected' : ''} onClick={() => setFilter(f)}>{f}</button>
                ))}
              </div>
              {loading ? (
                <p className="modal-state" data-testid="materials-loading">A carregar os materiais…</p>
              ) : error ? (
                <div className="modal-state" data-testid="materials-error">Não foi possível carregar os materiais.<button data-testid="materials-retry-button" onClick={load}>Tentar novamente</button></div>
              ) : (
                <div className="file-list">
                  {filtered.map((f) => {
                    const isMedia = f.category === 'Vídeos' || f.category === 'Fotografias';
                    const Icon = f.category === 'Financeiros' ? FileSpreadsheet
                      : f.category === 'Vídeos' ? Film
                      : f.category === 'Fotografias' ? ImageIcon
                      : FileText;
                    return (
                      <div
                        key={f.id}
                        className={`file-row ${isMedia ? 'file-row-clickable' : ''}`}
                        role={isMedia ? 'button' : undefined}
                        tabIndex={isMedia ? 0 : undefined}
                        onClick={isMedia ? () => openMedia(f) : undefined}
                        onKeyDown={isMedia ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openMedia(f); } } : undefined}
                        data-testid={`material-${f.id}`}
                      >
                        <div className="file-icon"><Icon size={22} strokeWidth={1.4} /></div>
                        <div>
                          <h3>{f.title}</h3>
                          <p>{f.category} · {f.size > 1000000 ? `${(f.size / 1000000).toFixed(1)} MB` : `${Math.ceil(f.size / 1000)} KB`}</p>
                        </div>
                        {isMedia ? (
                          <button
                            type="button"
                            className="file-action"
                            aria-label={f.category === 'Vídeos' ? `Ver ${f.title}` : `Pré-visualizar ${f.title}`}
                            data-testid={`view-${f.id}-button`}
                            onClick={(e) => { e.stopPropagation(); openMedia(f); }}
                          >
                            {f.category === 'Vídeos' ? <Play size={17} /> : <ImageIcon size={17} />}
                          </button>
                        ) : (
                          <a className="file-action" href={asset(f.id, true)} target="_blank" rel="noopener noreferrer" aria-label={`Descarregar ${f.title}`} data-testid={`download-${f.id}-link`}>
                            <Download size={17} />
                          </a>
                        )}
                      </div>
                    );
                  })}
                  {filtered.length === 0 && <p className="modal-state" data-testid="materials-empty">Nenhum material encontrado. Experimente outro termo.</p>}
                </div>
              )}
              <a className="original-source" data-testid="materials-original-site-link" href="https://liofilandia.com/" target="_blank" rel="noopener noreferrer">Fonte pública · liofilandia.com <ExternalLink size={15} /></a>
              <p className="tiny-note" data-testid="materials-rights-note">Documentos e imagens do arquivo fornecido. Referências de terceiros não representam parcerias. Direitos de utilização das fotografias e vídeos por confirmar antes de divulgação pública.</p>
            </>
          )}
          {tab === 'gallery' && <ReferenceGallery />}
        </div>
        <MediaPreview preview={preview} onClose={() => setPreview(null)} />
      </DialogContent>
    </Dialog>
  );
};
